import { Button, Group, Stack, Text } from "@mantine/core";
import { useResizeObserver } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconLayoutGrid } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import GridLayout from "react-grid-layout/legacy";
import type { Layout, LayoutItem } from "react-grid-layout/legacy";
import { dashboardApi } from "../../lib/api";
import type {
  DashboardCatalog,
  DashboardLayout,
  DashboardLayoutItem,
  DashboardReportMeta,
  DashboardReportOption,
} from "../../types/api";
import {
  addReportToLayout,
  buildDefaultLayout,
  gridBottomPadding,
  removeReportFromLayout,
} from "./dashboardLayoutUtils";
import { ReportPicker } from "./ReportPicker";
import { WidgetRenderer } from "./WidgetRenderer";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./dashboard-grid.css";

interface DashboardGridProps {
  catalog: DashboardCatalog;
}

const COLS = 12;
const ROW_HEIGHT = 88;
const MARGIN: [number, number] = [12, 12];
const CONTAINER_PADDING: [number, number] = [0, 0];

function cloneLayout(layout: DashboardLayout): DashboardLayout {
  return {
    ...layout,
    items: layout.items.map((item) => ({ ...item })),
  };
}

function toGridLayout(items: DashboardLayoutItem[]): Layout {
  return items.map(
    (item): LayoutItem => ({
      i: item.report_key,
      x: item.x,
      y: item.y,
      w: item.colspan,
      h: item.rowspan,
      minW: 2,
      minH: 1,
      maxW: COLS,
      maxH: 8,
    }),
  );
}

function fromGridLayout(layout: Layout): DashboardLayoutItem[] {
  return layout.map((item) => ({
    report_key: item.i,
    x: item.x,
    y: item.y,
    colspan: item.w,
    rowspan: item.h,
  }));
}

export function DashboardGrid({ catalog }: DashboardGridProps) {
  const queryClient = useQueryClient();
  const [editMode, setEditMode] = useState(false);
  const [draftLayout, setDraftLayout] = useState<DashboardLayout>(() => cloneLayout(catalog.layout));
  const [containerRef, containerRect] = useResizeObserver();

  const reportMap = useMemo(() => {
    const map = new Map<string, DashboardReportMeta>();
    for (const report of catalog.reports) {
      map.set(report.key, report);
    }
    return map;
  }, [catalog.reports]);

  const saveMutation = useMutation({
    mutationFn: (layout: DashboardLayout) => dashboardApi.saveLayout(layout),
    onSuccess: (data) => {
      queryClient.setQueryData(["dashboard-catalog"], data);
      setDraftLayout(cloneLayout(data.layout));
      setEditMode(false);
      notifications.show({ color: "green", message: "Layout saved" });
    },
    onError: () => {
      notifications.show({ color: "red", message: "Failed to save layout" });
    },
  });

  const layout = editMode ? draftLayout : catalog.layout;
  const layoutKeys = useMemo(() => new Set(layout.items.map((item) => item.report_key)), [layout.items]);
  const gridLayout = useMemo(() => toGridLayout(layout.items), [layout.items]);
  const gridWidth = containerRect.width > 0 ? containerRect.width : 0;
  // Extra bottom room only while editing so resize handles aren't clipped.
  const bottomPad = editMode ? gridBottomPadding(layout.items, ROW_HEIGHT, MARGIN[1]) : 0;

  const handleLayoutChange = (next: Layout) => {
    if (!editMode) return;
    setDraftLayout((prev) => ({
      ...prev,
      items: fromGridLayout(next),
    }));
  };

  const handleAddReport = (option: DashboardReportOption) => {
    const meta = reportMap.get(option.key);
    if (!meta) return;
    setDraftLayout((prev) =>
      addReportToLayout(prev, {
        key: meta.key,
        colspan: option.default_colspan,
        rowspan: option.default_rowspan,
      }),
    );
  };

  const handleRemoveReport = (reportKey: string) => {
    setDraftLayout((prev) => removeReportFromLayout(prev, reportKey));
  };

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center" wrap="wrap">
        {editMode ? (
          <Group gap="xs" wrap="wrap">
            <Text size="xs" c="dimmed">
              Drag header to move · corner to resize
            </Text>
            <ReportPicker
              catalog={{ ...catalog, groups: catalog.groups ?? [] }}
              layoutKeys={layoutKeys}
              onAdd={handleAddReport}
            />
          </Group>
        ) : (
          <span />
        )}
        <Group gap="xs">
          {editMode ? (
            <>
              <Button
                variant="subtle"
                color="gray"
                size="xs"
                onClick={() => setDraftLayout(buildDefaultLayout(catalog.reports))}
              >
                Reset layout
              </Button>
              <Button variant="default" size="xs" onClick={() => setEditMode(false)}>
                Cancel
              </Button>
              <Button
                color="violet"
                size="xs"
                loading={saveMutation.isPending}
                onClick={() => saveMutation.mutate(draftLayout)}
              >
                Save
              </Button>
            </>
          ) : (
            <Button
              variant="subtle"
              color="gray"
              size="xs"
              leftSection={<IconLayoutGrid size="var(--axio-unit-14)" />}
              onClick={() => {
                setDraftLayout(cloneLayout(catalog.layout));
                setEditMode(true);
              }}
            >
              Customize
            </Button>
          )}
        </Group>
      </Group>

      <div
        ref={containerRef}
        className={editMode ? "dashboard-grid-wrap dashboard-grid-wrap--editing" : "dashboard-grid-wrap"}
        style={{ paddingBottom: bottomPad }}
      >
        {gridWidth > 0 && (
          <GridLayout
            className="layout"
            width={gridWidth}
            cols={COLS}
            rowHeight={ROW_HEIGHT}
            margin={MARGIN}
            containerPadding={CONTAINER_PADDING}
            layout={gridLayout}
            onLayoutChange={handleLayoutChange}
            isDraggable={editMode}
            isResizable={editMode}
            draggableHandle=".dashboard-drag-handle"
            resizeHandles={["se", "s", "e"]}
            compactType="vertical"
            preventCollision={false}
          >
            {layout.items.map((item) => {
              const report = reportMap.get(item.report_key);
              if (!report) return null;

              return (
                <div key={item.report_key} className="dashboard-grid-item">
                  <WidgetRenderer
                    report={report}
                    editMode={editMode}
                    onRemove={() => handleRemoveReport(item.report_key)}
                  />
                </div>
              );
            })}
          </GridLayout>
        )}
      </div>
    </Stack>
  );
}
